import { useEffect, useMemo } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Form } from "@patient-watch/ui";
import { formSearchParamsSchema } from "../../types/formSearchParams";
import FormStepWrapper from "../../components/FormStepWrapper";
import FormButtons from "../../components/FormButtons";
import { useFormData } from "../../hooks/useFormData";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import FormLoading from "../../components/FormLoading";
import IntervalFieldCards, { type IntervalErrorInfo } from "../../components/IntervalFieldCards";
import {
    HOUR_OPTIONS,
    IntervalFormValue,
    mapStoredIntervalsToForm,
    normaliseIntervalsForSave,
    sanitiseEndOptions,
} from "../../utils/timeIntervals";
import { useCopy } from "../../i18n/locale";
import type { GlucoseCopy } from "../../i18n/copy";

const createFormSchema = (messages: GlucoseCopy["intervals"]) => {
    const intervalSchema = z
        .object({
            start_time: z.string().min(1, messages.startRequired),
            end_time: z.string().min(1, messages.endRequired),
        })
        .superRefine(({ start_time, end_time }, ctx) => {
            if (start_time >= end_time) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: messages.endAfterStart,
                    path: ["end_time"],
                });
            }
        });

    return z
        .object({
            at_sea_intervals: z.array(intervalSchema).min(1, messages.atLeastOne),
        })
        .superRefine(({ at_sea_intervals }, ctx) => {
            const sorted = [...at_sea_intervals].sort((a, b) => a.start_time.localeCompare(b.start_time));

            let previousEnd: string | null = null;
            sorted.forEach((interval, index) => {
                if (previousEnd && interval.start_time < previousEnd) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: messages.noOverlap,
                        path: ["at_sea_intervals", index, "start_time"],
                    });
                }
                previousEnd = interval.end_time;
            });
        });
};

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

export const Route = createFileRoute("/form/at-sea-intervals")({
    component: AtSeaIntervalsComponent,
    validateSearch: (search) => formSearchParamsSchema.parse(search),
});

function AtSeaIntervalsComponent() {
    const { entry_id, profile_id } = useSearch({
        from: "/form/at-sea-intervals",
    });
    const navigate = useNavigate();
    const copy = useCopy();

    const form = useForm<FormData>({
        resolver: zodResolver(createFormSchema(copy.intervals)),
        defaultValues: {
            at_sea_intervals: [{ start_time: "", end_time: "" }],
        },
    });

    const { control, watch, formState } = form;

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: "at_sea_intervals",
    });

    const { isLoading, entryData } = useFormData({
        entry_id,
        form,
        fields: [],
    });

    useEffect(() => {
        if (!entryData || formState.isDirty) {
            return;
        }

        const initial = mapStoredIntervalsToForm(entryData.at_sea_intervals as IntervalFormValue[] | null | undefined, {
            fallbackFullDay: entryData.at_sea_all_day === true,
        });
        replace(initial);
    }, [entryData, replace, formState.isDirty]);

    useEffect(() => {
        if (entryData?.at_sea_all_day) {
            navigate({
                to: "/form/on-duty-intervals",
                search: { entry_id, profile_id } as any,
            });
        }
    }, [entryData, navigate, entry_id, profile_id]);

    const { submitting, submitForm } = useFormSubmission({
        entry_id,
        profile_id,
        nextRoute: "/form/on-duty-intervals",
    });

    const intervals = watch("at_sea_intervals") ?? [];

    const appendReason = (label: string, reason: string) => (label.includes(reason) ? label : `${label} (${reason})`);

    const computeStartOptions = ({
        index,
        intervals: currentIntervals,
    }: {
        index: number;
        intervals: IntervalFormValue[];
    }) => {
        if (index === 0) {
            return HOUR_OPTIONS.map((option) => ({ ...option }));
        }
        const previousEnd = currentIntervals[index - 1]?.end_time;
        if (!previousEnd) {
            return HOUR_OPTIONS.map((option) => ({ ...option }));
        }
        return HOUR_OPTIONS.map((option) =>
            option.value < previousEnd
                ? {
                      ...option,
                      locked: true,
                      label: appendReason(option.label, copy.intervals.afterPrevious),
                  }
                : { ...option }
        );
    };

    const computeEndOptions = ({
        index,
        intervals: currentIntervals,
    }: {
        index: number;
        intervals: IntervalFormValue[];
    }) => {
        const startValue = currentIntervals[index]?.start_time;
        const base = sanitiseEndOptions(startValue).map((option) => ({
            ...option,
        }));

        const nextStart = currentIntervals[index + 1]?.start_time;
        if (!nextStart) return base;

        return base.map((option) =>
            option.value > nextStart
                ? {
                      ...option,
                      locked: true,
                      label: appendReason(option.label, copy.intervals.beforeNext),
                  }
                : option
        );
    };

    const atSeaIntervalError = useMemo(() => {
        if (!formState.errors.at_sea_intervals) return null;
        if (Array.isArray(formState.errors.at_sea_intervals)) {
            const fieldError = formState.errors.at_sea_intervals.find(Boolean);
            if (fieldError && "message" in fieldError && fieldError.message) {
                return fieldError.message as string;
            }
        }

        if ("message" in formState.errors.at_sea_intervals && formState.errors.at_sea_intervals.message) {
            return formState.errors.at_sea_intervals.message as string;
        }

        return null;
    }, [formState.errors.at_sea_intervals]);

    const onSubmit = (data: FormData) => {
        const normalised = normaliseIntervalsForSave(data.at_sea_intervals);
        submitForm({
            at_sea_intervals: normalised,
            at_sea_all_day: false,
        });
    };

    const onPrevious = () => {
        navigate({
            to: "/form/at-sea-all-day",
            search: { entry_id, profile_id } as any,
        });
    };

    const handleAddInterval = () => {
        const lastInterval = intervals?.[intervals.length - 1];
        const defaultStart = lastInterval?.end_time ?? "";
        append({ start_time: defaultStart, end_time: "" });
    };

    const handleRemoveInterval = (index: number) => {
        if (fields.length === 1) {
            replace([{ start_time: "", end_time: "" }]);
            return;
        }
        remove(index);
    };

    const getIntervalErrors = (index: number): IntervalErrorInfo => {
        const errors = formState.errors.at_sea_intervals;
        const fieldError = Array.isArray(errors) ? errors[index] : undefined;

        const startError =
            fieldError && "start_time" in fieldError
                ? (fieldError.start_time as { message?: string })?.message
                : undefined;

        const endError =
            fieldError && "end_time" in fieldError ? (fieldError.end_time as { message?: string })?.message : undefined;

        return { startError, endError };
    };

    const canRemoveInterval = (index: number) => {
        if (fields.length === 1) {
            const interval = intervals?.[index];
            return Boolean(interval?.start_time || interval?.end_time);
        }
        return true;
    };

    if (isLoading) {
        return (
            <FormStepWrapper currentStep={2}>
                <FormLoading />
            </FormStepWrapper>
        );
    }

    return (
        <FormStepWrapper currentStep={2}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 py-4 w-full max-w-2xl">
                    <h2 className="block font-semibold text-xl text-center">{copy.intervals.question}</h2>

                    <IntervalFieldCards
                        namePrefix="at_sea_intervals"
                        fields={fields as any}
                        intervals={intervals as IntervalFormValue[]}
                        computeStartOptions={computeStartOptions}
                        computeEndOptions={computeEndOptions}
                        getErrors={getIntervalErrors}
                        canRemove={canRemoveInterval}
                        onRemove={handleRemoveInterval}
                    />

                    <div className="flex justify-center">
                        <Button type="button" size="md" variant="secondary" onClick={handleAddInterval}>
                            {copy.intervals.add}
                        </Button>
                    </div>

                    {atSeaIntervalError && (
                        <div className="bg-red-50 mt-4 p-3 border border-red-200 rounded-lg">
                            <p className="text-red-800 text-sm">{atSeaIntervalError}</p>
                        </div>
                    )}

                    <FormButtons submitting={submitting} showPrevious onPrevious={onPrevious} />
                </form>
            </Form>
        </FormStepWrapper>
    );
}
